import { useState } from 'react'

export type ColumnWidths = Record<string, number>

const STORAGE_PREFIX = 'meratech:columns:'

/**
 * Ширины колонок из хранилища: только конечные положительные числа по строковым ключам,
 * всё остальное — мусор, и таблица стартует шириной по умолчанию.
 */
function readWidths(tableKey: string): ColumnWidths {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableKey)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}
    const widths: ColumnWidths = {}
    // Сужение внешнего JSON: поля проверяются по одному
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) widths[id] = value
    }
    return widths
  } catch {
    return {}
  }
}

/**
 * Ширины колонок таблицы, которые переживают перезагрузку: пользователь растянул «Наименование»
 * в реестре каталога — и завтра она такой же. Это настройка устройства, как тема и язык,
 * поэтому `localStorage`, а не адрес и не профиль на бэке (ручки под это нет — `BE-…` заводить
 * рано: настройка личная и лёгкая).
 *
 * Ключ — имя таблицы: у каталога, заказов и корзины свои наборы колонок и свои ширины.
 * Хранилище может быть недоступно (приватный режим, WebView без storage): чтение и запись
 * обёрнуты, без него ширины живут до перезагрузки.
 */
export function useColumnWidths(tableKey: string): [ColumnWidths, (next: ColumnWidths) => void] {
  const [widths, setWidths] = useState<ColumnWidths>(() => readWidths(tableKey))

  const update = (next: ColumnWidths) => {
    setWidths(next)
    try {
      localStorage.setItem(STORAGE_PREFIX + tableKey, JSON.stringify(next))
    } catch {
      /* Хранилище недоступно — ширины живут до перезагрузки, этого достаточно */
    }
  }

  return [widths, update]
}
